import {div, hide, show} from '@megaphonenpm/mputils/src/DOM.js';
import {MediaContainer} from './MediaContainer.js';
import {isEmpty} from '@megaphonenpm/mputils/src/utils.js'
import {EventDispatcher} from '@megaphonenpm/mputils/src/EventDispatcher.js';
import {MediaContainerEvent} from './MediaContainerEvent.js';

/**
 * A container for displaying an image or video within a specific, predictable browser-rendering frame.
 *
 * Background:
 * Normally, when an <img> element is in the DOM, and a script changes its .src, the timing with which
 * new image appears is unpredictable. The image might appear instantly, in the very next rendering
 * frame, or it might not appear for several frames, leaving the old .src on screen until the new image
 * appears. As a result, when the .src of multiple images are changed in the same browser frame loop,
 * the images actually often appear at slightly different times, creating flickering, empty gaps, and
 * "pop in" effects. Unlike a typical website, in MegaScreen, when moving from state to state, and
 * especially between scenes in a run, images and videos MUST appear at exactly the same time.
 * Further, there can be no "empty frames" between states or between scenes. For example, given two
 * UGC scenes, one containing a photo of a sunset and the other containing a photo of mountains, if
 * the run advances from the sunset to the mountains, MegaScreen must continue to display the sunset
 * on screen until the mountains appear. There must be no gap between the sunset and the mountains
 * where nothing is displayed in the browser. Further, the sunset must not appear in the
 * mountain-photo's scene. The entire contents of the sunset-photo's scene--including the backplate,
 * the user's name and city, and all media--MUST disappear within a single browser rendering frame,
 * and the very next frame must contain the entire contents of the mountain-photo's scene. In a
 * typical website, content elements, including images and video, load progressively when they
 * become available, so users tolerate the variable timing with which an image appears after its
 * .src is changed. In MegaScreen's television-graphics environment, empty frames are not tolerated.
 *
 * Solution:
 * To force all content, including images and videos, to appear at the same time, MegaScreen uses
 * a double-buffering system that relies on the DoubleBufferedMediaContainer class.
 * DoubleBufferedMediaContainer uses two MediaContainer objects to prepare media for on-screen
 * display. When media is ready, DoubleBufferedMediaContainer's setURI() method resolves a promise,
 * and the caller can then choose to make the media visible by invoking
 * DoubleBufferedMediaContainer's render() method. When all images and videos in an
 * application are managed via DoubleBufferedMediaContainer objects, then the application can
 * guarantee that all media appears in precisely the same browser-rendering frame, without any empty
 * gaps, "pop ins", or flickering.
 *
 * Example 1: Render a single image when it becomes available
 *
 * let sceneMediaContainer = new DoubleBufferedMediaContainer('sceneMedia');
 * sceneMediaContainer.setURI(sceneMediaURI).then(() => {
 *   sceneMediaContainer.render();
 * });
 *
 * Example 2: Render two images in the same frame after both become available
 *
 * let mediaReadyPromises = [];
 * let foregroundMediaContainer = new DoubleBufferedMediaContainer('foregroundMedia');
 * let backgroundMediaContainer = new DoubleBufferedMediaContainer('backgroundMedia');
 *
 * // Assign the desired media (image or video)
 * mediaReadyPromises.push(foregroundMediaContainer.setURI(foregroundMediaURI));
 * mediaReadyPromises.push(backgroundMediaContainer.setURI(backgroundMediaURI));
 *
 * // When all media is ready for on-screen display, render it to the screen. The images will
 * // appear at exactly the same time.
 * Promise.all(mediaReadyPromises).then(() => {
 *   foregroundMediaContainer.render();
 *   backgroundMediaContainer.render();
 * });
 *
 * Implementation:
 * Internally, DoubleBufferedMediaContainer uses two MediaContainer objects as display buffers. When
 * one image is loading (i.e., is in one buffer), the other image is displayed on screen. When the
 * loading image is ready, the caller can display it on screen using render(). The render() method
 * then uses the CSS .display property to "swap the buffers" by hiding the old image and showing
 * the new image.
 *
 * DoubleBufferedMediaContainer's API is identical to MediaContainer except that it will not display
 * any content until and unless render() is invoked.
 *
 */
export class DoubleBufferedMediaContainer extends EventDispatcher {

  /**
   * Constructor.
   *
   * @param name An identifier used for debugging and to give the DoubleBufferedMediaContainer's
   *             view element an id in the DOM.
   */
  constructor (name) {
    super();

    this.name = name;
    this.uri = null;
    this.front = null;
    this.back = null;
    this.view = null;
    this.currentBuffer;

    this.init();
  }

  /**
   * Object initialization.
   */
  init () {
    this.front = new MediaContainer(this.name);
    this.back = new MediaContainer(this.name);

    this.front.on(MediaContainerEvent.VIDEO_PLAY_REJECTED, this.videoPlayRejectedListener.bind(this));
    this.back.on(MediaContainerEvent.VIDEO_PLAY_REJECTED, this.videoPlayRejectedListener.bind(this));

    this.front.on(MediaContainerEvent.UNMUTED_VIDEO_PLAY_REJECTED, this.unmutedVideoPlayRejectedListener.bind(this));
    this.back.on(MediaContainerEvent.UNMUTED_VIDEO_PLAY_REJECTED, this.unmutedVideoPlayRejectedListener.bind(this));

    this.view = div(`doubleBuffered_${this.name}_mediaContainer`, 'mediaContainer');
    this.view.append(this.back.view, this.front.view);
    hide(this.back.view);
    hide(this.front.view);

    this.currentBuffer = this.front;

    this.back.view.style.width = '100%';
    this.back.view.style.height = '100%';

    this.front.view.style.width = '100%';
    this.front.view.style.height = '100%';
  }

  /**
   * Pass through for the MediaContainerEvent.VIDEO_PLAY_REJECTED event dispatched by the front and
   * back buffer MediaContainer objects.
   */
  videoPlayRejectedListener () {
    this.dispatchEvent(MediaContainerEvent.VIDEO_PLAY_REJECTED);
  }

  /**
   * Pass through for the MediaContainerEvent.UNMUTED_VIDEO_PLAY_REJECTED event dispatched by the
   * front and back buffer MediaContainer objects.
   */
  unmutedVideoPlayRejectedListener () {
    this.dispatchEvent(MediaContainerEvent.UNMUTED_VIDEO_PLAY_REJECTED);
  }

  /**
   * Assign the URI for an image or video to display on screen, causing the asset to load.
   *
   * @param uri A URI referring to an image or video.
   * @returns {Promise<unknown>} A promise that resolves when the image or video is ready to be
   *          displayed. To display the image or video, use render().
   */
  setURI (uri) {
    return new Promise((resolve) => {
      if (this.currentBuffer === this.back) {
        this.front.setURI(uri).then(() => {
          this.uri = uri;
          this.back.pause();
          this.currentBuffer = this.front;
          resolve();
        });
      } else {
        this.back.setURI(uri).then(() => {
          this.uri = uri;
          this.front.pause();
          this.currentBuffer = this.back;
          resolve();
        });
      }
    });
  }

  /**
   * Mute the media (applies to videos only). Pass through to front and back MediaContainer buffers.
   */
  setMuted (muted) {
    this.front.setMuted(muted);
    this.back.setMuted(muted)
  }

  /**
   * Enable or disable looped playback (applies to videos only). Pass through to front and back
   * MediaContainer buffers.
   */
  setLoop (loop) {
    this.front.setLoop(loop);
    this.back.setLoop(loop)
  }

  /**
   * Pause the media (applies to videos only). Pass through to front and back MediaContainer buffers.
   */
  pause () {
    this.front.pause();
    this.back.pause();
  }

  /**
   * Make the most recently buffered image or video visible. Until render() is called, the previous
   * image or video is displayed.
   */
  render () {
    hide(this.currentBuffer === this.front ? this.back.view : this.front.view);
    show( this.currentBuffer === this.front ? this.front.view : this.back.view);

    isEmpty(this.uri) ? hide(this.view) : show(this.view);
  }
}
